import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import type { ApiCourseProgress } from "../services/api/courseProgress";
import { LESSONS } from "../domain/help/course";
import {
  courseProgress, finishedOnStorageKey, localDate, parseFinished,
  parseFinishedOn, progressStorageKey, type CourseProgress,
} from "../domain/help/courseProgress";

export type UseCourseProgressResult = CourseProgress & {
  markDone: (slug: string) => void;
  memberId: string;
  finishedOn: string | undefined;
  /** Only show the course reminder once the account's progress has been checked. */
  progressReady: boolean;
  syncStatus: "checking" | "saving" | "saved" | "offline";
  retrySync: () => void;
};

type LocalProgress = { finished: Set<string>; finishedOn: string | undefined };
const scopeKey = (orgId: string, memberId: string, accountType: string) => `${encodeURIComponent(orgId)}.${accountType}.${encodeURIComponent(memberId)}`;

function readLocal(orgId: string, memberId: string, accountType: string): LocalProgress {
  if (!memberId) return { finished: new Set(), finishedOn: undefined };
  try {
    const scoped = scopeKey(orgId, memberId, accountType);
    const stored = localStorage.getItem(progressStorageKey(scoped));
    // Legacy browser progress had no organization. Bind its migration once, so
    // switching organizations cannot import the same record into another account.
    const bindingKey = `noticeboard.courseLegacyOrg.${memberId}`;
    const binding = localStorage.getItem(bindingKey);
    const boundAccount = `${accountType}:${orgId}`;
    const legacy = stored === null && (!binding || binding === orgId || binding === boundAccount);
    if (legacy && orgId) localStorage.setItem(bindingKey, boundAccount);
    const previousScoped = `${encodeURIComponent(orgId)}.${encodeURIComponent(memberId)}`;
    const key = legacy ? (localStorage.getItem(progressStorageKey(previousScoped)) !== null ? previousScoped : memberId) : scoped;
    return {
      finished: parseFinished(localStorage.getItem(progressStorageKey(key))),
      finishedOn: parseFinishedOn(localStorage.getItem(finishedOnStorageKey(key))),
    };
  } catch { return { finished: new Set(), finishedOn: undefined }; }
}

function writeLocal(orgId: string, memberId: string, progress: LocalProgress, accountType: string) {
  if (!memberId) return;
  try {
    const scoped = scopeKey(orgId, memberId, accountType);
    localStorage.setItem(progressStorageKey(scoped), JSON.stringify(Array.from(progress.finished)));
    if (progress.finishedOn) localStorage.setItem(finishedOnStorageKey(scoped), progress.finishedOn);
  } catch { /* In-memory progress still works when storage is unavailable. */ }
}

const mergeProgress = (a: ApiCourseProgress | undefined, b: ApiCourseProgress): ApiCourseProgress => ({
  ...b,
  completedLessons: Array.from(new Set([...(a?.completedLessons ?? []), ...b.completedLessons])),
  completed: !!a?.completed || b.completed,
  finishedOn: b.finishedOn ?? a?.finishedOn ?? null,
});

/** Account-backed progress with a browser copy for offline use and migration. */
export const useCourseProgress = (): UseCourseProgressResult => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const memberId = user ? String(user.id ?? user.email ?? "") : "";
  const orgId = String(user?.schoolId ?? user?.orgId ?? user?.soid ?? "").toUpperCase();
  const accountType = user?.userType ?? "account";
  const identity = scopeKey(orgId, memberId, accountType);
  const queryKey = useMemo(() => ["courseProgress", orgId, accountType, memberId], [orgId, accountType, memberId]);
  const enabled = !!orgId && !!memberId && !!token;
  const [saved, setSaved] = useState(() => ({ identity, ...readLocal(orgId, memberId, accountType) }));
  const local = saved.identity === identity ? saved : readLocal(orgId, memberId, accountType);

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async () => {
      if (!orgId || !token) throw new Error("Missing account details");
      let remote = await api.courseProgress.get(orgId, token);
      const browser = readLocal(orgId, memberId, accountType);
      const missing = Array.from(browser.finished).filter(slug => !remote.completedLessons.includes(slug));
      // Import previous browser progress, and retry any offline lesson completions.
      if (missing.length) {
        remote = await api.courseProgress.save(orgId, token, Array.from(browser.finished), browser.finishedOn);
      }
      remote = mergeProgress(queryClient.getQueryData<ApiCourseProgress>(queryKey), remote);
      writeLocal(orgId, memberId, {
        finished: parseFinished(JSON.stringify(remote.completedLessons)),
        finishedOn: parseFinishedOn(remote.finishedOn),
      }, accountType);
      return remote;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (progress: { completedLessons: string[]; finishedOn?: string }) => {
      if (!token || !orgId) throw new Error("Missing account details");
      return api.courseProgress.save(orgId, token, progress.completedLessons, progress.finishedOn);
    },
    onSuccess: remote => {
      // Responses can arrive out of order; never replace newer ticks with older ones.
      queryClient.setQueryData<ApiCourseProgress>(queryKey, previous => {
        const merged = mergeProgress(previous, remote);
        writeLocal(orgId, memberId, { finished: new Set(merged.completedLessons), finishedOn: parseFinishedOn(merged.finishedOn) }, accountType);
        return merged;
      });
    },
    onError: () => {
      showMessage({ message: "Your course progress is kept in this browser, but couldn't sync to your account. Please try again when you're online.", severity: "error" });
    },
  });

  const finished = useMemo(() => {
    // Once the server is loaded its lesson set includes migrated browser progress.
    const slugs = query.data?.completed ? LESSONS.map(lesson => lesson.slug) : query.data?.completedLessons;
    return slugs ? new Set(slugs) : local.finished;
  }, [query.data, local.finished]);
  const progress = courseProgress(finished);
  const finishedOn = progress.nextUp ? undefined : parseFinishedOn(query.data?.finishedOn ?? null) ?? local.finishedOn ?? localDate(new Date());

  const markDone = useCallback((slug: string) => {
    if (!memberId || !LESSONS.some(lesson => lesson.slug === slug)) return;
    const cached = queryClient.getQueryData<ApiCourseProgress>(queryKey);
    const current = cached?.completedLessons ?? Array.from(readLocal(orgId, memberId, accountType).finished);
    const next = new Set(current).add(slug);
    const date = !courseProgress(next).nextUp ? finishedOn ?? localDate(new Date()) : undefined;
    writeLocal(orgId, memberId, { finished: next, finishedOn: date }, accountType);
    setSaved({ identity, finished: next, finishedOn: date });
    if (enabled) {
      const optimistic: ApiCourseProgress = {
        courseId: "noticeboard-fundamentals-v1", completedLessons: Array.from(next),
        completed: !!cached?.completed || !!date, finishedOn: cached?.finishedOn ?? date ?? null,
      };
      queryClient.setQueryData(queryKey, optimistic);
      mutation.mutate({ completedLessons: Array.from(next), ...(date ? { finishedOn: date } : {}) });
    }
  }, [memberId, orgId, accountType, identity, enabled, finishedOn, queryClient, queryKey, mutation]);

  return {
    ...progress, markDone, memberId, finishedOn,
    progressReady: !enabled || query.isSuccess,
    syncStatus: !enabled || query.isError || mutation.isError ? "offline" : query.isPending ? "checking" : mutation.isPending ? "saving" : "saved",
    retrySync: () => { mutation.reset(); void query.refetch(); },
  };
};
